import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuotationPreview = ({ quotation, onEdit, onConvertToInvoice, onDuplicate, onDelete }) => {
  const statusConfig = {
    draft: { label: 'Borrador', color: 'bg-secondary-100 text-secondary-600', icon: 'FileText' },
    sent: { label: 'Enviado', color: 'bg-primary-100 text-primary-600', icon: 'Send' },
    approved: { label: 'Aprobado', color: 'bg-success-100 text-success-600', icon: 'CheckCircle' },
    expired: { label: 'Expirado', color: 'bg-error-100 text-error-600', icon: 'Clock' }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('es-ES').format(new Date(date));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    console.log('Download PDF functionality would be implemented here');
  };

  const statusInfo = statusConfig[quotation.status];

  return (
    <div className="bg-surface rounded-lg border border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <h2 className="text-lg font-heading font-semibold text-text-primary">
              {quotation.number}
            </h2>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
              <div className="flex items-center space-x-1">
                <Icon name={statusInfo.icon} size={14} />
                <span>{statusInfo.label}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              iconName="Printer"
              onClick={handlePrint}
            />
            <Button
              variant="ghost"
              size="sm"
              iconName="Download"
              onClick={handleDownloadPDF}
            />
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              onClick={onEdit}
            >
              Editar
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {quotation.status === 'approved' && (
            <Button
              variant="primary"
              size="sm"
              iconName="Receipt"
              onClick={onConvertToInvoice}
            >
              Convertir a Factura
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            iconName="Copy"
            onClick={onDuplicate}
          >
            Duplicar
          </Button>
          
          {quotation.status === 'draft' && (
            <Button
              variant="danger"
              size="sm"
              iconName="Trash2"
              onClick={onDelete}
            >
              Eliminar
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto bg-white">
          {/* Company Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-text-primary">
                  Everforce Manager
                </h1>
                <p className="text-text-secondary">Gestión Empresarial Integral</p>
              </div>
            </div>
          </div>

          {/* Document Title */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
              PRESUPUESTO
            </h2>
            <p className="text-text-secondary">
              Número: {quotation.number}
            </p>
          </div>

          {/* Client and Date Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="font-medium text-text-primary mb-3">Cliente:</h3>
              <div className="bg-secondary-50 p-4 rounded-lg">
                <p className="font-medium text-text-primary">{quotation.clientName}</p>
                <p className="text-text-secondary text-sm mt-1">
                  Cliente ID: {quotation.clientId}
                </p>
              </div>
            </div>
            
            <div>
              <h3 className="font-medium text-text-primary mb-3">Información:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Fecha:</span>
                  <span className="text-text-primary">{formatDate(quotation.date)}</span>
                </div>
                {quotation.expiryDate && (
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Vencimiento:</span>
                    <span className="text-text-primary">{formatDate(quotation.expiryDate)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-text-secondary">Estado:</span>
                  <span className={`px-2 py-1 rounded text-xs ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <h3 className="font-medium text-text-primary mb-4">Detalle de Productos:</h3>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-secondary-50 px-4 py-3 border-b border-border">
                <div className="grid grid-cols-12 gap-4 text-sm font-medium text-text-primary">
                  <div className="col-span-1">#</div>
                  <div className="col-span-5">Producto</div>
                  <div className="col-span-2 text-center">Cantidad</div>
                  <div className="col-span-2 text-right">Precio Unit.</div>
                  <div className="col-span-2 text-right">Total</div>
                </div>
              </div>
              
              <div className="divide-y divide-border">
                {quotation.items.map((item, index) => (
                  <div key={item.id} className="px-4 py-3">
                    <div className="grid grid-cols-12 gap-4 items-center text-sm">
                      <div className="col-span-1 text-text-muted">
                        {index + 1}
                      </div>
                      <div className="col-span-5">
                        <div className="font-medium text-text-primary">
                          {item.productName}
                        </div>
                        <div className="text-xs text-text-muted">
                          Código: {item.productCode}
                        </div>
                      </div>
                      <div className="col-span-2 text-center text-text-primary">
                        {item.quantity}
                      </div>
                      <div className="col-span-2 text-right text-text-primary">
                        {formatCurrency(item.unitPrice)}
                      </div>
                      <div className="col-span-2 text-right font-medium text-text-primary">
                        {formatCurrency(item.total)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-full max-w-md">
              <div className="bg-secondary-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Subtotal:</span>
                  <span className="text-text-primary">{formatCurrency(quotation.subtotal)}</span>
                </div>
                
                {quotation.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Descuento ({quotation.discount}%):</span>
                    <span className="text-error">-{formatCurrency(quotation.discountAmount)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Base Imponible:</span>
                  <span className="text-text-primary">{formatCurrency(quotation.taxableAmount)}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">IVA ({quotation.taxRate}%):</span>
                  <span className="text-text-primary">{formatCurrency(quotation.taxAmount)}</span>
                </div>
                
                <div className="border-t border-border pt-2 mt-3">
                  <div className="flex justify-between font-semibold text-lg">
                    <span className="text-text-primary">TOTAL:</span>
                    <span className="text-primary">{formatCurrency(quotation.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {quotation.notes && (
            <div className="mb-8">
              <h3 className="font-medium text-text-primary mb-3">Notas:</h3>
              <div className="bg-secondary-50 p-4 rounded-lg">
                <p className="text-text-secondary text-sm whitespace-pre-wrap">
                  {quotation.notes}
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-xs text-text-muted border-t border-border pt-4">
            <p>Este presupuesto es válido hasta la fecha de vencimiento indicada.</p>
            <p className="mt-1">Everforce Manager - Sistema de Gestión Empresarial</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationPreview;